import React, { useState } from 'react';
import { invoiceTemplatesService } from '../../services/invoiceTemplatesService';
import { getKsefStatusBadge, getDateRange } from './ksefHelpers';

const CorrectionsTab = ({
  invoices,
  ksefEnabled,
  ksefSending,
  onSendToKsef,
  onDownloadUpo,
  setError
}) => {
  // Filters
  const [filterSearch, setFilterSearch] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Filter corrections from all invoices
  const corrections = invoices.filter(inv => inv.typ_faktury === 'korekta');

  const filteredCorrections = corrections.filter(invoice => {
    if (filterPeriod && filterPeriod !== 'all') {
      if (filterPeriod === 'custom') {
        if (filterDateFrom) {
          const fromDate = new Date(filterDateFrom);
          const invoiceDate = new Date(invoice.data_wystawienia);
          if (invoiceDate < fromDate) return false;
        }
        if (filterDateTo) {
          const toDate = new Date(filterDateTo);
          toDate.setHours(23, 59, 59, 999);
          const invoiceDate = new Date(invoice.data_wystawienia);
          if (invoiceDate > toDate) return false;
        }
      } else {
        const { from, to } = getDateRange(filterPeriod);
        if (from && to) {
          const invoiceDate = new Date(invoice.data_wystawienia);
          if (invoiceDate < from || invoiceDate > to) return false;
        }
      }
    }

    if (filterCustomer) {
      const customerMatch = (invoice.nabywca_nazwa || '').toLowerCase().includes(filterCustomer.toLowerCase()) ||
                           (invoice.nabywca_nip || '').includes(filterCustomer);
      if (!customerMatch) return false;
    }

    if (filterSearch) {
      const searchLower = filterSearch.toLowerCase();
      const matchNumber = (invoice.numer_faktury || '').toLowerCase().includes(searchLower);
      const matchCustomer = (invoice.nabywca_nazwa || '').toLowerCase().includes(searchLower);
      if (!matchNumber && !matchCustomer) return false;
    }

    return true;
  });

  const resetFilters = () => {
    setFilterPeriod('all');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterCustomer('');
    setFilterSearch('');
  };

  const activeFiltersCount = [
    filterPeriod !== 'all' ? 1 : 0,
    filterCustomer ? 1 : 0,
    filterSearch ? 1 : 0
  ].reduce((a, b) => a + b, 0);

  const downloadInvoicePDF = async (invoiceId, invoiceNumber) => {
    try {
      const blob = await invoiceTemplatesService.getInvoicePdf(invoiceId);

      if (blob.type === 'application/pdf' || blob.size > 100) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `faktura_${invoiceNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setError('Plik PDF nie zostal wygenerowany poprawnie. Sprawdz logi serwera.');
      }
    } catch (err) {
      setError('Blad pobierania PDF: ' + err.message);
    }
  };

  return (
    <div style={{
      backgroundColor: 'var(--card-bg)',
      borderRadius: '4px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      overflow: 'hidden'
    }}>
      <div style={{
        border: '1px solid var(--primary)', color: 'var(--primary)' ,
        padding: '0.5rem 0.75rem',
        fontSize: '12px',
        fontWeight: '600',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span>📝 Faktury korygujace ({filteredCorrections.length})</span>
        <button
          onClick={() => setShowFilters(!showFilters)}
          style={{
            padding: '4px 10px',
            fontSize: '11px',
            // Tokeny motywu: bialy tekst na polprzezroczystym tle byl niewidoczny w jasnym motywie
            backgroundColor: activeFiltersCount > 0 ? 'var(--warning)' : 'var(--card-bg)',
            border: activeFiltersCount > 0 ? '1px solid var(--warning)' : '1px solid var(--border)',
            color: activeFiltersCount > 0 ? '#000' : 'var(--text-secondary)'
          }}
        >
          🔍 Filtry {activeFiltersCount > 0 && `(${activeFiltersCount})`}
        </button>
      </div>

      {/* Panel filtrow dla korekt */}
      {showFilters && (
        <div style={{
          padding: '0.75rem',
          backgroundColor: 'var(--bg)',
          borderBottom: '1px solid var(--border)'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '0.5rem',
            marginBottom: '0.5rem'
          }}>
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>Szukaj</label>
              <input
                type="text"
                placeholder="Nr korekty lub klient..."
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>Okres</label>
              <select
                value={filterPeriod}
                onChange={(e) => setFilterPeriod(e.target.value)}
                style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
              >
                <option value="all">Wszystkie</option>
                <option value="today">Dzisiaj</option>
                <option value="week">Ostatnie 7 dni</option>
                <option value="month">Ten miesiac</option>
                <option value="year">Ten rok</option>
                <option value="custom">Zakres dat...</option>
              </select>
            </div>
            {filterPeriod === 'custom' && (
              <>
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>Od</label>
                  <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)}
                    style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }} />
                </div>
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>Do</label>
                  <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)}
                    style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }} />
                </div>
              </>
            )}
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>Klient</label>
              <input
                type="text"
                placeholder="Nazwa lub NIP..."
                value={filterCustomer}
                onChange={(e) => setFilterCustomer(e.target.value)}
                style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
              />
            </div>
          </div>
          {activeFiltersCount > 0 && (
            <button onClick={resetFilters}
              style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--danger)', backgroundColor: 'var(--card-bg)', color: 'var(--danger)' }}>
              {'✕'} Wyczysc filtry
            </button>
          )}
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--bg)' }}>
              <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid var(--border)', fontWeight: '600' }}>Nr korekty</th>
              <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid var(--border)', fontWeight: '600' }}>Data</th>
              <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid var(--border)', fontWeight: '600' }}>Nabywca</th>
              <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid var(--border)', fontWeight: '600' }}>NIP</th>
              <th style={{ padding: '0.5rem', textAlign: 'right', borderBottom: '1px solid var(--border)', fontWeight: '600' }}>Kwota</th>
              {ksefEnabled && (
                <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '1px solid var(--border)', fontWeight: '600' }}>KSeF</th>
              )}
              <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '1px solid var(--border)', fontWeight: '600' }}>Akcje</th>
            </tr>
          </thead>
          <tbody>
            {filteredCorrections.length === 0 ? (
              <tr>
                <td colSpan={ksefEnabled ? "7" : "6"} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  Brak faktur korygujacych
                </td>
              </tr>
            ) : (
              filteredCorrections.map((correction) => (
                <tr key={correction.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '0.5rem' }}>
                    <span style={{ fontWeight: '500', color: 'var(--danger)' }}>{correction.numer_faktury}</span>
                  </td>
                  <td style={{ padding: '0.5rem' }}>
                    {correction.data_wystawienia ? new Date(correction.data_wystawienia).toLocaleDateString('pl-PL') : '-'}
                  </td>
                  <td style={{ padding: '0.5rem' }}>
                    {correction.nabywca_nazwa || '-'}
                  </td>
                  <td style={{ padding: '0.5rem' }}>
                    {correction.nabywca_nip || '-'}
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '500', color: 'var(--danger)' }}>
                    {parseFloat(correction.suma_brutto || 0).toFixed(2)} zl
                  </td>
                  {ksefEnabled && (
                    <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                      {getKsefStatusBadge(correction) || (
                        <span style={{ color: 'var(--text-muted)', fontSize: '10px' }}>-</span>
                      )}
                    </td>
                  )}
                  <td style={{ padding: '0.5rem', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => downloadInvoicePDF(correction.id, correction.numer_faktury)}
                      title="Pobierz PDF"
                      style={{
                        padding: '4px 10px',
                        fontSize: '11px',
                        border: '1px solid var(--success)',
                        backgroundColor: 'var(--card-bg)',
                        color: 'var(--success)',
                        marginRight: '4px'
                      }}
                    >
                      📄 PDF
                    </button>
                    {ksefEnabled && (!correction.ksef_status || correction.ksef_status === 'rejected' || correction.ksef_status === 'error') && (
                      <button
                        onClick={() => onSendToKsef(correction.id)}
                        disabled={ksefSending[correction.id]}
                        title={correction.ksef_status === 'rejected' ? 'Wyslij ponownie do KSeF' : 'Wyslij korekta do KSeF'}
                        style={{
                          padding: '4px 10px',
                          fontSize: '11px',
                          border: '1px solid var(--info)',
                          backgroundColor: ksefSending[correction.id] ? 'var(--border)' : 'var(--card-bg)',
                          color: 'var(--info)',
                          cursor: ksefSending[correction.id] ? 'wait' : undefined,
                          opacity: ksefSending[correction.id] ? 0.7 : 1
                        }}
                      >
                        {ksefSending[correction.id] ? '⏳' : (correction.ksef_status === 'rejected' || correction.ksef_status === 'error' ? '🔄 KSeF' : '📤 KSeF')}
                      </button>
                    )}
                    {ksefEnabled && correction.ksef_status === 'accepted' && (
                      <button
                        onClick={() => onDownloadUpo(correction.id, correction.ksef_numer)}
                        title="Pobierz UPO"
                        style={{
                          padding: '4px 10px',
                          fontSize: '11px',
                          border: '1px solid var(--text-muted)',
                          backgroundColor: 'var(--card-bg)',
                          color: 'var(--text-muted)'
                        }}
                      >
                        📋 UPO
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CorrectionsTab;
